import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { CRUD_ACTION_LABELS, CRUD_MODULES, groupPermissions, permissionLabel } from '~/services/access.service'
import { type PermissionType, permissionService, type RoleType } from '~/services/permission.service'
import { type UserDetailType, userService } from '~/services/user.service'
import { resolveError } from '~/utils/error'

export interface UserPermissionsTabPropsType {
  /** La ficha del usuario cargada; null mientras llega. */
  user: () => UserDetailType | null | undefined
  /** El id del usuario editado. */
  userId: () => number
  /** Recarga la ficha tras guardar. */
  onRefetch: () => void
  /** Si quien edita puede asignar roles y permisos. */
  canAssign: boolean
}

/** Pestaña de rol y permisos: el rol base más los permisos directos del usuario. */
const UserPermissionsTab: Component<UserPermissionsTabPropsType> = props => {
  const toast = useToast()
  const alert = useAlert()

  const [roles] = createResource(async () => {
    try {
      return await permissionService.listRoles()
    } catch {
      return [] as RoleType[]
    }
  })

  const [allPermissions] = createResource(async () => {
    try {
      return await permissionService.listPermissions()
    } catch {
      return [] as PermissionType[]
    }
  })

  const [selectedRole, setSelectedRole] = createSignal<string>('')
  const [directPermissions, setDirectPermissions] = createSignal<Set<number>>(new Set<number>())
  const [savingPermissions, setSavingPermissions] = createSignal(false)

  // El estado editable se siembra cuando llega la ficha: antes de eso el
  // formulario mostraría permisos vacíos y guardar encima los borraría.
  createEffect(() => {
    const data = props.user()
    if (!data) return
    setSelectedRole(data.roleId ? String(data.roleId) : '')
    setDirectPermissions(new Set(data.permissions.map(p => p.id)))
  })

  const roleOptions = () => [{ value: '', title: 'Sin rol' }, ...(roles() ?? []).map(r => ({ value: String(r.id), title: r.name, subtitle: r.description ?? undefined }))]

  /** Permisos que otorga el rol seleccionado (no los guardados): la matriz reacciona al cambiarlo. */
  const inheritedIds = createMemo(() => {
    const id = Number(selectedRole())
    if (!id) return new Set<number>()
    const role = roles()?.find(r => r.id === id)
    if (role?.permissions) return new Set(role.permissions.map(p => p.id))
    // El rol guardado no viene en la lista (rol global sin permisos cargados):
    // se usan los que trajo la ficha.
    return new Set((props.user()?.rolePermissions ?? []).map(p => p.id))
  })

  const permissionGroups = createMemo(() => groupPermissions(allPermissions() ?? []))

  const isChecked = (id: number) => inheritedIds().has(id) || directPermissions().has(id)

  const toggleDirect = (permission: PermissionType) => {
    if (inheritedIds().has(permission.id)) return // lo da el rol: se quita desde el rol
    const next = new Set(directPermissions())
    if (next.has(permission.id)) {
      next.delete(permission.id)
      // Regla del backend: eliminar exige actualizar. Al quitar "editar" se cae "eliminar".
      if (permission.name.endsWith('.update')) {
        const del = (allPermissions() ?? []).find(p => p.name === `${permission.name.slice(0, -'.update'.length)}.delete`)
        if (del) next.delete(del.id)
      }
    } else {
      next.add(permission.id)
      if (permission.name.endsWith('.delete')) {
        const upd = (allPermissions() ?? []).find(p => p.name === `${permission.name.slice(0, -'.delete'.length)}.update`)
        if (upd && !inheritedIds().has(upd.id)) next.add(upd.id)
      }
    }
    setDirectPermissions(next)
  }

  const effectiveCount = createMemo(() => new Set([...inheritedIds(), ...directPermissions()]).size)

  const savePermissions = async () => {
    setSavingPermissions(true)
    try {
      const nextRole = selectedRole() ? Number(selectedRole()) : null
      if (nextRole !== (props.user()?.roleId ?? null)) {
        await userService.update(props.userId(), { roleId: nextRole })
      }
      await userService.syncPermissions(props.userId(), [...directPermissions()])
      toast.addToast({ title: 'Permisos actualizados', type: 'success' })
      props.onRefetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSavingPermissions(false)
    }
  }

  /**
   * Quita todos los permisos asignados manualmente a este usuario, dejando
   * sólo los que otorga su rol. El endpoint de sincronización se llama con la
   * lista vacía.
   */
  const clearDirectPermissions = async () => {
    const count = directPermissions().size
    if (count === 0) return
    const ok = await alert.confirm({
      type: 'warning',
      title: 'Limpiar permisos directos',
      message: `Se quitarán los ${count} permisos asignados manualmente a este usuario. Los que otorga su rol se conservan.`,
      confirmText: 'Sí, limpiar',
      confirmColor: 'danger',
      confirmIcon: 'material-symbols-light:delete-sweep-outline'
    })
    if (!ok) return
    setSavingPermissions(true)
    try {
      await userService.syncPermissions(props.userId(), [])
      setDirectPermissions(new Set<number>())
      toast.addToast({ title: 'Permisos directos eliminados', type: 'success' })
      props.onRefetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSavingPermissions(false)
    }
  }

  return (
    <div class="space-y-6">
      <Card>
        <Card.Title>
          <div class="flex items-center">
            <Icon icon="material-symbols-light:shield-outline" size={20} class="mr-2" />
            <h2 class="text-lg font-semibold">Rol de permisos</h2>
          </div>
        </Card.Title>
        <Card.Body>
          <p class="mb-4 text-sm text-foreground-subtle">
            El rol es la base: reparte los permisos comunes. Debajo puedes añadir permisos sueltos sólo para este usuario, sin tocar el rol de los demás.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Rol" placeholder="Sin rol" options={roleOptions()} value={selectedRole()} onChange={v => setSelectedRole(String(v ?? ''))} disabled={!props.canAssign} />
            <div class="flex items-end gap-2 text-sm text-foreground-subtle">
              <Chip variant="soft" color="primary">
                {inheritedIds().size} del rol
              </Chip>
              <Chip variant="soft" color="info">
                {directPermissions().size} directos
              </Chip>
              <Chip variant="soft" color="success">
                {effectiveCount()} efectivos
              </Chip>
            </div>
          </div>
          <Show when={props.user()?.isRoot}>
            <div class="mt-4 rounded-lg border border-danger-soft bg-danger-soft/20 px-3 py-2 text-sm text-foreground">Es super administrador: puede todo sin importar lo que se marque aquí.</div>
          </Show>
        </Card.Body>
      </Card>

      <Card>
        <Card.Title>
          <div class="flex items-center">
            <Icon icon="material-symbols-light:lock-open-outline" size={20} class="mr-2" />
            <h2 class="text-lg font-semibold">Permisos especiales</h2>
          </div>
        </Card.Title>
        <Card.Body>
          <p class="mb-4 text-sm text-foreground-subtle">Los que ya da el rol salen marcados y bloqueados: para quitarlos, se edita el rol. Eliminar exige poder editar, así que se marcan juntos.</p>

          <div class="space-y-4">
            <For each={permissionGroups()}>
              {group => (
                <div class="rounded-lg border border-border p-4">
                  <h4 class="mb-3 font-medium text-foreground">{group.label}</h4>

                  {/* Módulo CRUD: una columna por acción */}
                  <Show when={CRUD_MODULES[group.module] && group.permissions.length >= 3}>
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <For each={['read', 'create', 'update', 'delete']}>
                        {action => {
                          const perm = group.permissions.find(p => p.name.endsWith(`.${action}`))
                          return (
                            <Show when={perm}>
                              {p => (
                                <Switch
                                  size="sm"
                                  label={CRUD_ACTION_LABELS[action]}
                                  checked={isChecked(p().id)}
                                  disabled={inheritedIds().has(p().id) || !props.canAssign}
                                  tooltip={inheritedIds().has(p().id) ? 'Lo otorga el rol' : undefined}
                                  onChange={() => toggleDirect(p())}
                                />
                              )}
                            </Show>
                          )
                        }}
                      </For>
                    </div>
                  </Show>

                  {/* Permisos especiales: lista simple */}
                  {/* Los interruptores son inline: sin flex-col dos permisos largos quedan pegados en la misma línea. */}
                  <Show when={!CRUD_MODULES[group.module] || group.permissions.length < 3}>
                    <div class="flex flex-col gap-3">
                      <For each={group.permissions}>
                        {perm => (
                          <Switch
                            size="sm"
                            label={permissionLabel(perm)}
                            checked={isChecked(perm.id)}
                            disabled={inheritedIds().has(perm.id) || !props.canAssign}
                            tooltip={inheritedIds().has(perm.id) ? 'Lo otorga el rol' : undefined}
                            onChange={() => toggleDirect(perm)}
                          />
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>

          <div class="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <Show when={directPermissions().size > 0}>
              <Button
                type="button"
                variant="soft"
                color="danger"
                prependIcon="material-symbols-light:delete-sweep-outline"
                onClick={clearDirectPermissions}
                disabled={savingPermissions() || !props.canAssign}
              >
                Limpiar permisos directos
              </Button>
            </Show>
            <Button type="button" onClick={savePermissions} disabled={savingPermissions() || !props.canAssign}>
              {savingPermissions() ? 'Guardando...' : 'Guardar permisos'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

export default UserPermissionsTab
